import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Loader2, ExternalLink, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";

type Kind = "idea" | "project";

interface Props {
  kind: Kind;
}

const formatDate = (iso?: string | null) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
};

const PublicShare = ({ kind }: Props) => {
  const { slug } = useParams<{ slug: string }>();
  const rpcName = kind === "idea" ? "get_public_idea_share" : "get_public_project_share";

  const { data, isLoading } = useQuery({
    queryKey: ["public-share", kind, slug],
    queryFn: async () => {
      const { data, error } = await supabase.rpc(rpcName as any, { _slug: slug });
      if (error) throw error;
      return data as any;
    },
    enabled: !!slug,
  });

  useEffect(() => {
    if (data) {
      document.title = `${data.title} | emrkt.co`;
      trackEvent("stakeholder_dashboard_view", {
        entity_type: kind,
        entity_id: data.id,
      });
    }
  }, [data, kind]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <SiteHeader />
        <main className="pt-32 pb-20 container mx-auto px-6 max-w-2xl text-center">
          <h1 className="font-display text-2xl font-bold mb-2">This share link isn't active</h1>
          <p className="text-muted-foreground mb-6">
            The owner may have turned off public sharing, or the link is incorrect.
          </p>
          <Button asChild variant="outline">
            <Link to="/">Go to emrkt.co</Link>
          </Button>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const updates: any[] = data.updates ?? [];
  const asks: any[] = data.asks ?? [];
  const lessons: any[] = data.lessons ?? [];
  const revisions: any[] = data.revisions ?? [];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-primary mb-2">
              <Sparkles className="h-3.5 w-3.5" />
              {kind === "project" ? "Project Snapshot" : "Idea Snapshot"}
              {data.status && (
                <span className="text-muted-foreground normal-case tracking-normal">· {data.status}</span>
              )}
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold">{data.title}</h1>
            <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-muted-foreground">
              {data.author && <span>By {data.author}</span>}
              {data.category && <Badge variant="secondary">{data.category}</Badge>}
              {data.created_at && <span>Started {formatDate(data.created_at)}</span>}
              {kind === "project" && data.score_band && (
                <Badge className="bg-primary/15 text-primary border-primary/30">
                  Readiness · {data.score_band}
                </Badge>
              )}
            </div>
            {Array.isArray(data.tags) && data.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {data.tags.map((t: string) => (
                  <span key={t} className="text-xs text-muted-foreground border border-border rounded-full px-2 py-0.5">
                    #{t}
                  </span>
                ))}
              </div>
            )}
          </div>

          {data.image_url && (
            <img
              src={data.image_url}
              alt=""
              className="w-full rounded-xl border border-border mb-8 max-h-96 object-cover"
            />
          )}

          {data.description && (
            <section className="mb-10">
              <h2 className="font-display text-lg font-semibold mb-2">Summary</h2>
              <p className="text-base text-foreground/90 whitespace-pre-wrap leading-relaxed">
                {data.description}
              </p>
            </section>
          )}

          {updates.length > 0 && (
            <section className="mb-10">
              <h2 className="font-display text-lg font-semibold mb-3">Latest updates</h2>
              <ol className="space-y-4">
                {updates.slice(0, 8).map((u: any) => (
                  <li key={u.id} className="border border-border rounded-xl p-4 bg-card">
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <div className="text-sm font-semibold">{u.title}</div>
                      <div className="text-xs text-muted-foreground">{formatDate(u.created_at)}</div>
                    </div>
                    {u.body && (
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">{u.body}</p>
                    )}
                  </li>
                ))}
              </ol>
            </section>
          )}

          {kind === "project" && lessons.length > 0 && (
            <section className="mb-10">
              <h2 className="font-display text-lg font-semibold mb-3">Lessons learned</h2>
              <ul className="space-y-3">
                {lessons.map((l: any, i: number) => (
                  <li key={i} className="border border-border rounded-xl p-4 bg-card">
                    <div className="text-xs uppercase tracking-wider text-primary mb-1">{l.outcome}</div>
                    <p className="text-sm text-foreground/90 whitespace-pre-wrap">{l.summary}</p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {asks.length > 0 && (
            <section className="mb-10">
              <h2 className="font-display text-lg font-semibold mb-3">Requested from stakeholders</h2>
              <ul className="space-y-3">
                {asks.map((a: any) => (
                  <li key={a.id} className="border border-border rounded-xl p-4 bg-card">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="text-[10px] uppercase">{a.kind}</Badge>
                      <span className="text-sm font-semibold">{a.headline}</span>
                    </div>
                    {a.details && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{a.details}</p>}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {revisions.length > 0 && (
            <section className="mb-10">
              <h2 className="font-display text-lg font-semibold mb-3">History</h2>
              <ol className="space-y-2 border-l border-border pl-4">
                {revisions.slice(0, 12).map((r: any) => (
                  <li key={r.revision_number} className="text-sm">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="font-mono">v{r.revision_number}</span>
                      <span>·</span>
                      <span>{r.change_kind}</span>
                      <span>·</span>
                      <span>{formatDate(r.changed_at)}</span>
                    </div>
                    {r.diff_summary && <div className="text-foreground/90">{r.diff_summary}</div>}
                  </li>
                ))}
              </ol>
            </section>
          )}

          {/* About emrkt strip */}
          <div className="mt-12 border-t border-border pt-8 flex flex-col md:flex-row md:items-center gap-4 justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-primary mb-1">Shared via emrkt.co</div>
              <p className="text-sm text-muted-foreground max-w-lg">
                emrkt.co is an Alabama government affairs firm powering the work that moves
                communities forward.
              </p>
            </div>
            <Button asChild variant="outline">
              <a href="https://emrkt.co" target="_blank" rel="noreferrer">
                Learn more <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
              </a>
            </Button>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default PublicShare;